/**
 * Equipos en taller.
 *
 * La cifra que manda no es el estado: son los **días fuera**. Mientras un
 * extintor está en el taller, el titular no tiene la protección que la
 * norma le exige en ese punto — salvo que se le haya dejado un
 * provisional, que es justo la decisión que esta pantalla ayuda a tomar.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { dayOf } from '../table/format.js';
import { WorkshopActions, type WorkshopOrderRef } from './workshop-actions.js';

interface WorkshopRow {
  id: string;
  number: string;
  status: string;
  reason: string;
  internal_code: string;
  customer_name: string;
  site_name: string;
  withdrawn_at: string;
  ready_at: string | null;
  days_out: number;
}

const REASON: Record<string, string> = {
  RETIMBRADO: 'Retimbrado',
  RECARGA: 'Recarga',
  REPARACION: 'Reparación',
  REVISION_FABRICANTE: 'Revisión de fabricante',
  ANOMALIA: 'Anomalía',
  GARANTIA: 'Garantía',
  OTROS: 'Otros',
};

const STATUS: Record<string, { label: string; tone: string }> = {
  INSTALADO: { label: 'Instalado', tone: 'ok' },
  RETIRADO: { label: 'Retirado', tone: 'warn' },
  EN_VEHICULO: { label: 'En vehículo', tone: 'info' },
  RECIBIDO: { label: 'Recibido', tone: 'info' },
  PENDIENTE_DIAGNOSTICO: { label: 'Pendiente de diagnóstico', tone: 'warn' },
  PENDIENTE_REPARACION: { label: 'Pendiente de reparación', tone: 'warn' },
  PENDIENTE_MATERIAL: { label: 'Pendiente de material', tone: 'warn' },
  EN_REPARACION: { label: 'En reparación', tone: 'info' },
  REPARADO: { label: 'Reparado', tone: 'ok' },
  LISTO_ENTREGA: { label: 'Listo para entrega', tone: 'ok' },
  EN_TRANSPORTE: { label: 'En transporte', tone: 'info' },
  REINSTALADO: { label: 'Reinstalado', tone: 'ok' },
  SUSTITUIDO: { label: 'Sustituido', tone: 'off' },
  BAJA: { label: 'De baja', tone: 'off' },
  UNKNOWN: { label: 'Sin clasificar', tone: 'off' },
};

const COLUMNS: Column<WorkshopRow>[] = [
  { id: 'number', label: 'Nº', render: (r) => r.number, sortable: true, width: '10rem' },
  { id: 'internalCode', label: 'Equipo', render: (r) => r.internal_code, width: '8rem' },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name },
  {
    id: 'reason',
    label: 'Motivo',
    render: (r) => REASON[r.reason] ?? r.reason,
    exportValue: (r) => REASON[r.reason] ?? r.reason,
  },
  {
    id: 'status',
    label: 'Estado',
    width: '13rem',
    sortable: true,
    render: (r) => {
      const s = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      return <span className={`pill pill--${s.tone}`}>{s.label}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  {
    id: 'withdrawnAt',
    label: 'Retirado',
    width: '8rem',
    sortable: true,
    render: (r) => dayOf(r.withdrawn_at),
    exportValue: (r) => dayOf(r.withdrawn_at),
  },
  {
    id: 'daysOut',
    label: 'Días fuera',
    numeric: true,
    width: '8rem',
    render: (r) =>
      r.days_out > 30 ? (
        <span className="due due--late">⚠ {r.days_out}</span>
      ) : r.days_out > 14 ? (
        <span className="due due--soon">◷ {r.days_out}</span>
      ) : (
        <span className="due">{r.days_out}</span>
      ),
    exportValue: (r) => r.days_out,
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'open', label: 'Fuera del centro', param: { openOnly: 'true' } },
  { id: 'ready', label: 'Listos para devolver', param: { readyToReturn: 'true' } },
  { id: 'all', label: 'Todos', param: {} },
] as const;

export function WorkshopScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const [selected, setSelected] = useState<WorkshopOrderRef | null>(null);

  const listing = useListing<WorkshopRow>({
    path: '/workshop/orders',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Taller</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'open' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} equipos</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={[
          ...COLUMNS,
          {
            id: 'gestion',
            label: '',
            width: '7rem',
            render: (r: WorkshopRow) => (
              <button
                className="button button--quiet"
                type="button"
                onClick={() => setSelected(r as unknown as WorkshopOrderRef)}
              >
                Gestionar
              </button>
            ),
            exportValue: () => '',
          },
        ]}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="taller"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay equipos en taller."
      />

      <WorkshopActions order={selected} onDone={() => listing.reload()} />
    </section>
  );
}
